import { useEffect, useRef, useState } from 'react';
import { deleteQueryLogs } from '@api/delete-query-logs';
import { fetchQuery } from '@api/fetch-query';
import { storeQueryLog } from '@api/store-query-log';
import { HexalInstance } from '@common/enums';
import {
  IMessage,
  INlpRequest,
  INlpResponse,
  NlpRequestType,
  IQuestionOption,
  ITopicGroup,
  MessageType,
  TNullable,
} from '@common/types';
import { ChatLayout } from '@components/chat-layout';
import { ChatStream } from '@components/chat-stream';
import { QuestionInput } from '@components/question-input';
import { SelectTopic } from '@components/select-topic';
import { Button } from '@components/ui/button';
import { HttpStatus } from '@constants/http';
import { Pages } from '@constants/pages';
import useChat from '@hooks/use-chat';
import useConversation from '@hooks/use-conversation';
import useIsMobile from '@hooks/use-is-mobile';
import {
  convertQueryToMessage,
  convertResponseToMessage,
  CURRENT_INSTANCE,
  findFirstTopicGroupInQuery,
  findFirstTopicInQuery,
  getExactGroupFromTopicGroupsList,
  getExactTopicFromTopicsList,
  getOnlyDisabledTopicFromGroup,
  normalizeQueryForTopicMatching,
  QUERY_PARAM,
  questionOptionToMessage,
  SUBMIT_QUERY_PARAM,
  shouldUseMaxHight,
  shouldUseSessionStorage,
  trimQuery,
} from '@lib/utils';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

function ChatPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const {
    agreedToDisclaimer,
    agreedToDisclaimerPerSession,
    setShowAgreementBanner,
    topics,
    topicGroups,
    selectedTopic,
    setSelectedTopic,
    messages,
    addMessage,
    addResponse,
    responses,
  } = useChat();
  const { CONVERSATION_ID } = useConversation();
  const [query, setQuery] = useState<string>('');
  const [topicGroup, setTopicGroup] = useState<TNullable<ITopicGroup>>(null);
  const [queryIncludesTopicGroup, setQueryIncludesTopicGroup] = useState<boolean>(false);
  const [pendingQueryLogId, setPendingQueryLogId] = useState<TNullable<string>>(null);
  const [request, setRequest] = useState<TNullable<INlpRequest>>(null);
  const [showTopicsError, setShowTopicsError] = useState<boolean>(false);
  const [showManualTopicPicker, setShowManualTopicPicker] = useState<boolean>(false);
  const [response, setResponse] = useState<INlpResponse | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const messageCounterRef = useRef<number>(messages.length * 2);

  function buildTopicPickerUrl() {
    const params = new URLSearchParams();
    const trimmedQuery = trimQuery(query);

    if (trimmedQuery) {
      params.set(QUERY_PARAM, trimmedQuery);
    }

    return params.toString() ? `${Pages.TopicPicker}?${params.toString()}` : Pages.TopicPicker;
  }

  useEffect(() => {
    const hasAcceptedDisclaimer =
      (shouldUseSessionStorage() && agreedToDisclaimerPerSession) ||
      (!shouldUseSessionStorage() && agreedToDisclaimer);

    if (!hasAcceptedDisclaimer || !query) {
      return;
    }

    async function handleDisclaimerApprovedQuery() {
      const trimmedQuery = trimQuery(query);

      if (!trimmedQuery) {
        setQuery('');
        return;
      }

      if (!selectedTopic) {
        const normalizedQuery = normalizeQueryForTopicMatching(trimmedQuery);
        // If query is an exact topic
        const foundTopic = getExactTopicFromTopicsList(topics, normalizedQuery);
        if (foundTopic) {
          if (foundTopic.status === 'disabled') {
            handleStaticMessage(trimmedQuery, MessageType.UserEntry);
            handleStaticMessage(
              t('chat.response.disabled_topic', { topic: foundTopic.displayName }),
              MessageType.DisabledTopicMessage
            );
            setQuery('');
            return;
          }
          handleStaticMessage(trimmedQuery, MessageType.UserEntry);
          setSelectedTopic(foundTopic.id);
          handleStaticMessage(
            t('chat.response.topic_changed', { topic: foundTopic.displayName }),
            MessageType.Message
          );
          setQuery('');
          return;
        }
        // If query is an exact group
        if (topicGroups.length > 0) {
          const foundTopicGroup = getExactGroupFromTopicGroupsList(topicGroups, normalizedQuery);
          if (foundTopicGroup) {
            const disabledTopicFromGroup = getOnlyDisabledTopicFromGroup(topics, foundTopicGroup);
            if (disabledTopicFromGroup) {
              handleStaticMessage(trimmedQuery, MessageType.UserEntry);
              handleStaticMessage(
                t('chat.response.disabled_topic', { topic: foundTopicGroup.name }),
                MessageType.DisabledTopicMessage
              );
              setQuery('');
              return;
            }
            await ensurePendingQueryLog(trimmedQuery);
            setTopicGroup(foundTopicGroup);
            setShowTopicsError(true);
            return;
          }
        }
        // If query includes a topic
        const firstTopic = findFirstTopicInQuery(normalizedQuery, topics);
        if (firstTopic) {
          if (firstTopic.status === 'disabled') {
            await handleQuerySend(
              trimmedQuery,
              firstTopic.id,
              t('chat.response.disabled_topic', { topic: firstTopic.displayName }),
              MessageType.DisabledTopicMessage
            );
            setShowTopicsError(false);
            return;
          }
          setSelectedTopic(firstTopic!.id);
          await handleQuerySend(trimmedQuery, firstTopic!.id);
          setShowTopicsError(false);
          return;
        }
        // If query includes a group
        if (topicGroups.length > 0) {
          const firstTopicGroup = findFirstTopicGroupInQuery(normalizedQuery, topicGroups);
          if (firstTopicGroup) {
            const disabledTopicFromGroup = getOnlyDisabledTopicFromGroup(topics, firstTopicGroup);
            if (disabledTopicFromGroup) {
              await handleQuerySend(
                trimmedQuery,
                disabledTopicFromGroup.id,
                t('chat.response.disabled_topic', { topic: firstTopicGroup.name }),
                MessageType.DisabledTopicMessage
              );
              setShowTopicsError(false);
              return;
            }
            await ensurePendingQueryLog(trimmedQuery);
            setTopicGroup(firstTopicGroup);
            setQueryIncludesTopicGroup(true);
            setShowTopicsError(true);
            return;
          }
        }
        // to topic & no topic group
        await ensurePendingQueryLog(trimmedQuery);
        setShowTopicsError(true);
        return;
      }
      if (topicGroup) {
        handleStaticMessage(trimmedQuery, MessageType.UserEntry);
        const foundTopic = topics.find((topic) => topic.id === selectedTopic);
        if (foundTopic) {
          handleStaticMessage(
            t('chat.response.topic_changed', { topic: foundTopic.displayName }),
            MessageType.Message
          );
          if (queryIncludesTopicGroup) await handleQuerySend(trimmedQuery, selectedTopic);
          else setQuery('');
        }
        return;
      }
      await handleQuerySend(trimmedQuery, selectedTopic);
    }

    void handleDisclaimerApprovedQuery();
  }, [agreedToDisclaimer, agreedToDisclaimerPerSession]); // eslint-disable-line

  async function ensurePendingQueryLog(queryToLog: string) {
    if (pendingQueryLogId) {
      return;
    }

    const queryLog = await storeQueryLog(queryToLog);
    if (queryLog?.id) {
      setPendingQueryLogId(queryLog.id);
    }
  }

  async function clearPendingQueryLog() {
    if (!pendingQueryLogId) {
      return;
    }

    const queryLogId = pendingQueryLogId;
    setPendingQueryLogId(null);

    const isDeleted = await deleteQueryLogs([queryLogId]);
    if (!isDeleted) {
      console.error(`[chat-page]: Failed to delete query log with id ${queryLogId}`);
    }
  }

  async function handleTopicSelect(topicId: string) {
    setSelectedTopic(topicId);
    setShowTopicsError(false);
    if (
      (shouldUseSessionStorage() && !agreedToDisclaimerPerSession) ||
      (!shouldUseSessionStorage() && !agreedToDisclaimer)
    ) {
      if (shouldUseMaxHight()) {
        const params = new URLSearchParams({
          [QUERY_PARAM]: query,
          [SUBMIT_QUERY_PARAM]: 'true',
        });
        navigate(`${Pages.Disclaimer}?${params.toString()}`);
      } else {
        setShowAgreementBanner(true);
      }
      return;
    }
    if (topicGroup) {
      const trimmedQuery = trimQuery(query);
      handleStaticMessage(trimmedQuery, MessageType.UserEntry);
      const foundTopic = topics.find((topic) => topic.id === topicId);
      if (foundTopic) {
        await clearPendingQueryLog();
        handleStaticMessage(
          t('chat.response.topic_changed', { topic: foundTopic.displayName }),
          MessageType.Message
        );
        if (queryIncludesTopicGroup) await handleQuerySend(trimmedQuery, topicId);
        else setQuery('');
      }
      return;
    }
    await clearPendingQueryLog();
    await handleQuerySend(trimQuery(query), topicId);
  }

  function handleManualTopicPickerOpen() {
    setTopicGroup(null);
    setQueryIncludesTopicGroup(false);
    if (CURRENT_INSTANCE.name === HexalInstance.Default) {
      navigate(buildTopicPickerUrl());
      return;
    }
    setShowManualTopicPicker(true);
  }

  function handleManualTopicPickerCancel() {
    setTopicGroup(null);
    setQueryIncludesTopicGroup(false);
    setShowManualTopicPicker(false);
  }

  function handleManualTopicSelect(topicId: string) {
    const foundTopic = topics.find((topic) => topic.id === topicId);
    setSelectedTopic(topicId);
    setTopicGroup(null);
    setQueryIncludesTopicGroup(false);
    setShowManualTopicPicker(false);

    if (foundTopic) {
      handleStaticMessage(
        t('chat.response.topic_changed', { topic: foundTopic.displayName }),
        MessageType.Message
      );
    }
  }

  function addMessageDelayed(message: IMessage, delay: number = 1000) {
    setLoading(true);
    setTimeout(() => {
      addMessage(message);
      setLoading(false);
    }, delay);
  }
  function handleMoreAnswersShow(messageId: number) {
    const currentResponse = responses.find((item) => item.id === messageId);
    if (currentResponse) {
      const choices = currentResponse?.response.questionOptions;
      if (!choices) {
        return;
      }

      const leftOverChoices = choices.slice(1);
      addMessageDelayed(
        {
          id: ++messageCounterRef.current,
          message: t('chat.request.show_more'),
          type: MessageType.UserEntry,
        },
        0
      );
      addMessageDelayed(
        {
          id: ++messageCounterRef.current,
          message: t('chat.response.perhaps_one_of_these'),
          choices: leftOverChoices,
          type: MessageType.ChoiceResponse,
        },
        2000
      );
    }
  }
  function handleRequestError(result: INlpResponse, fallbackQuery: string) {
    if (result.status === HttpStatus.BadRequest) {
      handleStaticMessage('', MessageType.InvalidQuery);
      setLoading(false);
      return;
    }
    if (result.status === HttpStatus.NotFound) {
      setResponse(result);
      messageCounterRef.current += 1;
      const message = questionOptionToMessage(
        {
          status: 'infoservice',
          questionId: `random-${messageCounterRef.current}`,
          optionType: 'UNCERTAIN_OPTION',
          actualQuery: fallbackQuery,
          answer: '',
          paraphrases: [],
          sourceDocuments: [],
          similarity: 1,
          references: [],
        },
        [],
        `random-${messageCounterRef.current}`,
        t,
        messageCounterRef.current
      );
      addMessage(message);
      addResponse(messageCounterRef.current, result);
      setLoading(false);
      return;
    }
    if (result.status === HttpStatus.InternalServerError) {
      handleStaticMessage(result.error || t('common.general_error'), MessageType.Error);
      setLoading(false);
    }
  }
  async function handleChoiceSelect(choice: IQuestionOption | undefined) {
    if (!choice) {
      addMessageDelayed({
        id: ++messageCounterRef.current,
        type: MessageType.UserEntry,
        message: t('chat.request.contact_info_service'),
      });
      addMessageDelayed(
        { id: ++messageCounterRef.current, type: MessageType.ForceMedInfo, message: '' },
        2000
      );
      return;
    }
    if (response?.questionOptions) {
      response.questionOptions = response.questionOptions.filter((c) => c !== choice);
    }
    addMessage({
      id: ++messageCounterRef.current,
      type: MessageType.UserEntry,
      message: t('chat.request.show_question') + choice.actualQuery,
    });

    const request: INlpRequest = {
      query: choice.actualQuery,
      topicId: selectedTopic,
      instanceId: CURRENT_INSTANCE.name,
      conversationId: CONVERSATION_ID,
      queryType: NlpRequestType.ChoiceSelection,
    };

    setLoading(true);
    setRequest(request);
    const result = await fetchQuery(request, t);
    if (!result) {
      setLoading(false);
      return;
    }

    if (result.error) {
      handleRequestError(result, choice.actualQuery);
      return;
    }

    addMessage(
      questionOptionToMessage(choice, [], response?.query?.id || '', t, ++messageCounterRef.current)
    );
    setLoading(false);
  }
  function handleStaticMessage(message: string, type: MessageType) {
    messageCounterRef.current += 1;
    addMessage({
      message,
      type,
      id: messageCounterRef.current,
    });
  }
  async function handleQuerySend(
    query: string,
    topicId: string,
    staticResponseMessage?: string,
    staticResponseType: MessageType = MessageType.DisabledTopicMessage
  ) {
    const trimmedQuery = trimQuery(query);
    if (!trimmedQuery) {
      return;
    }

    setLoading(true);
    setResponse(null);
    const request: INlpRequest = {
      query: trimmedQuery,
      topicId,
      instanceId: CURRENT_INSTANCE.name,
      conversationId: CONVERSATION_ID,
      queryType: NlpRequestType.Simple,
    };
    messageCounterRef.current += 1;
    addMessage(convertQueryToMessage(trimmedQuery, messageCounterRef.current));
    messageCounterRef.current += 1;
    setRequest(request);
    setQuery('');
    const result = await fetchQuery(request, t);
    if (result) {
      if (staticResponseMessage) {
        setResponse(result);
        messageCounterRef.current += 1;
        addMessage({
          id: ++messageCounterRef.current,
          message: staticResponseMessage,
          type: staticResponseType,
        });
        addResponse(messageCounterRef.current, result);
        setLoading(false);
        return;
      }

      if (result.error) {
        handleRequestError(result, trimmedQuery);
        return;
      }
      setResponse(result);
      messageCounterRef.current += 1;
      const message = convertResponseToMessage(result, ++messageCounterRef.current, t);
      addMessage(message);
      addResponse(messageCounterRef.current, result);
      setLoading(false);
    }
  }

  const desktopTopicPickerControls = !isMobile ? (
    <div className="mt-3 w-full">
      <Button
        variant="outline"
        size="sm"
        className="border-white bg-white text-xircle-main hover:bg-slate-100 hover:text-xircle-main"
        onClick={handleManualTopicPickerOpen}
      >
        {selectedTopic ? t('common.change_topic') : t('common.select_topic')}
      </Button>
    </div>
  ) : null;

  return (
    <ChatLayout
      addStaticMessage={handleStaticMessage}
      setQuery={setQuery}
      topicPickerControls={desktopTopicPickerControls}
    >
      <ChatStream
        request={request}
        onShowMoreAnswers={handleMoreAnswersShow}
        onSelectChoice={handleChoiceSelect}
        isLoading={isLoading}
      />
      {showManualTopicPicker && (
        <SelectTopic
          topics={topics}
          topicGroup={null}
          selectTopic={handleManualTopicSelect}
          onCancel={handleManualTopicPickerCancel}
        />
      )}
      <QuestionInput
        query={query}
        setQuery={setQuery}
        setShowTopicsError={setShowTopicsError}
        topics={topics}
        topicGroups={topicGroups}
        setTopicGroup={setTopicGroup}
        setQueryIncludesTopicGroup={setQueryIncludesTopicGroup}
        addStaticMessage={handleStaticMessage}
        sendQuery={handleQuerySend}
        clearPendingQueryLog={clearPendingQueryLog}
        ensurePendingQueryLog={ensurePendingQueryLog}
        disabled={isLoading || showTopicsError}
      />
      {isMobile && !showManualTopicPicker && (
        <div className="mt-3 flex justify-end px-2">
          <Button
            variant="outline"
            size="sm"
            className="border-white bg-white text-xircle-main hover:bg-slate-100 hover:text-xircle-main"
            onClick={handleManualTopicPickerOpen}
          >
            {selectedTopic ? t('common.change_topic') : t('common.select_topic')}
          </Button>
        </div>
      )}
      {showTopicsError && (
        <SelectTopic
          topics={topics}
          topicGroup={topicGroup}
          selectTopic={(topicId) => void handleTopicSelect(topicId)}
        />
      )}
    </ChatLayout>
  );
}

export default ChatPage;
